import { useEffect, useRef, useState } from "react";
import { Annotations, api, candidatesOf, clock, Peaks, Track } from "../api";
import { AppEpisode, date, language, route } from "./api";
import { useApp } from "./context";
import {
  Caption,
  DeviceButton,
  cueLanguage,
  cueText,
  currentTrack,
  PlayButton,
  trackIdentity,
  Transport,
  usePlayer,
} from "./player";
import { Artwork, Empty, Icon, IconButton, LanguageSelect } from "./visual";
import { EpisodeGrid, SectionHead } from "./Discover";
import Spectrogram from "./Spectrogram";
import { bandcampSearch } from "./spectrum";
export function NoteList({ recordingId }: { recordingId?: string }) {
  const { notes, editNote, deleteNote, t, catalogue, notify, title, lang } =
      useApp(),
    p = usePlayer();
  const [editing, setEditing] = useState<string | null>(null),
    [text, setText] = useState(""),
    [busy, setBusy] = useState(false);
  const items = notes.filter(
    (n) => !recordingId || n.recording_id === recordingId,
  );
  return items.length ? (
    <div className="notes-list">
      {items.map((n) => {
        const ep = catalogue.episodes.find(
          (e) => e.recording?.id === n.recording_id,
        );
        return (
          <article className="saved-note" key={n.id}>
            <div className="note-top">
              <button
                className="time-tag"
                disabled={!ep}
                title={
                  !ep
                    ? "This note belongs to an older recording version."
                    : undefined
                }
                onClick={() => {
                  if (ep) {
                    p.load(ep, n.time_ms);
                    location.hash = route("episode", ep.id, n.time_ms);
                  }
                }}
              >
                {clock(n.time_ms)}
              </button>
              <small>{date(n.created_at, lang)}</small>
            </div>
            {!recordingId && ep && (
              <a
                href={route("episode", ep.id, n.time_ms)}
                className="note-episode"
              >
                {title(ep)}
              </a>
            )}
            {editing === n.id ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setBusy(true);
                  try {
                    await editNote(n.id, text);
                    setEditing(null);
                  } catch (err) {
                    notify((err as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <textarea
                  aria-label={t("edit")}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={4000}
                  required
                />
                <div className="button-row">
                  <button className="button" disabled={busy}>
                    {t("save")}
                  </button>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => setEditing(null)}
                  >
                    {t("cancel")}
                  </button>
                </div>
              </form>
            ) : (
              <p dir="auto">{n.text}</p>
            )}
            <div className="note-actions">
              <button
                onClick={() => {
                  setEditing(n.id);
                  setText(n.text);
                }}
              >
                {t("edit")}
              </button>
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await deleteNote(n.id);
                  } catch (err) {
                    notify((err as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t("delete")}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  ) : (
    <Empty>{t("noNotes")}</Empty>
  );
}
function TrackRow({
  track,
  index,
  active,
  onSeek,
}: {
  track: Track;
  index: number;
  active: boolean;
  onSeek: (ms: number) => void;
}) {
  const { catalogue, t } = useApp();
  const identity = trackIdentity(track),
    artist = catalogue.artists.find(
      (a) => a.name.toLowerCase() === identity.artist.toLowerCase(),
    ),
    candidates = candidatesOf(track);
  const isCandidate = track.identity_status === "candidate";
  const bandcamp = bandcampSearch(identity.artist, identity.title);
  return (
    <div
      className={"track-row " + (active ? "active" : "")}
      aria-current={active ? "true" : undefined}
    >
      <span className="track-number">
        {active ? (
          <span className="equalizer">
            <i />
            <i />
            <i />
          </span>
        ) : (
          String(index + 1).padStart(2, "0")
        )}
      </span>
      <div className="track-content">
        <button
          className="track-title"
          disabled={track.start_ms === null}
          onClick={() => onSeek(track.start_ms!)}
        >
          {identity.title || t("unknown")}
        </button>
        <div>
          {artist ? (
            <a href={route("artist", artist.id)}>{identity.artist}</a>
          ) : (
            <span>{identity.artist || "—"}</span>
          )}
        </div>
        {bandcamp && (
          <a
            className="bandcamp-link"
            href={bandcamp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t("bandcamp")} · ${identity.artist} — ${identity.title}`}
          >
            {t("bandcamp")} <Icon name="arrow" size={18} />
          </a>
        )}
        {isCandidate && candidates[0] && (
          <details>
            <summary>{t("details")}</summary>
            <p>
              {[candidates[0].album, candidates[0].label]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <p>
              {candidates[0].catalogue?.provider === "musicbrainz"
                ? "MusicBrainz catalogue match"
                : "From the presenter’s announcement"}{" "}
              · {candidates[0].when}
            </p>
          </details>
        )}
      </div>
      <button
        className="time-tag"
        disabled={track.start_ms === null}
        onClick={() => onSeek(track.start_ms!)}
      >
        {track.timing_status.startsWith("estimated") ? "≈ " : ""}
        {clock(track.start_ms)}
      </button>
    </div>
  );
}
export default function EpisodePage({
  episode,
  at,
}: {
  episode: AppEpisode;
  at: number | undefined;
}) {
  const {
      t,
      appearance,
      lang,
      title,
      description,
      localization,
      catalogue,
      user,
      addNote,
      notify,
    } = useApp(),
    p = usePlayer();
  const [annotations, setAnnotations] = useState<Annotations | null>(null),
    [peaks, setPeaks] = useState<Peaks | null>(null),
    [error, setError] = useState(""),
    [bilingual, setBilingual] = useState(false),
    [follow, setFollow] = useState(appearance.followPlayback),
    [followTracks, setFollowTracks] = useState(appearance.followPlayback),
    [query, setQuery] = useState(""),
    [note, setNote] = useState(""),
    [saving, setSaving] = useState(false);
  const pane = useRef<HTMLDivElement>(null),
    transport = useRef<HTMLDivElement>(null),
    activeCue = useRef<HTMLButtonElement>(null),
    trackPane = useRef<HTMLDivElement>(null);
  const active = p.episode?.id === episode.id,
    ms = active ? p.ms : 0,
    duration = active ? p.duration : episode.recording?.duration_ms || 0;
  const data = active && p.annotations ? p.annotations : annotations,
    cue = data?.cues.find((c) => c.start_ms <= ms && ms < c.end_ms),
    track = data ? currentTrack(data.tracks, ms) : undefined;
  useEffect(() => {
    let valid = true;
    setAnnotations(null);
    setPeaks(null);
    setError("");
    api
      .annotations(episode.id, false)
      .then((v) => {
        if (valid) setAnnotations(v);
      })
      .catch((e) => {
        if (valid) setError(e.message);
      });
    api
      .peaks(episode.id)
      .then((v) => {
        if (valid) setPeaks(v);
      })
      .catch(() => {});
    return () => {
      valid = false;
    };
  }, [episode.id]);
  useEffect(() => {
    setQuery("");
    setNote("");
  }, [episode.id]);
  useEffect(() => {
    p.load(episode, at, false);
  }, [episode.id, at]);
  useEffect(() => {
    const element = transport.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        p.setOffscreenEpisode(
          !entry.isIntersecting && entry.boundingClientRect.bottom <= 0
            ? episode.id
            : null,
        );
      },
      { threshold: 0 },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      p.setOffscreenEpisode(null);
    };
  }, [episode.id, p.setOffscreenEpisode]);
  const reducedMotion =
    appearance.motion === "reduced" ||
    matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    setFollow(appearance.followPlayback);
    setFollowTracks(appearance.followPlayback);
  }, [appearance.followPlayback]);
  useEffect(() => {
    if (follow && activeCue.current && pane.current) {
      pane.current.scrollTo({
        top: activeCue.current.offsetTop - pane.current.clientHeight / 3,
        behavior: reducedMotion ? "instant" : "smooth",
      });
    }
  }, [cue?.id, follow, query, reducedMotion]);
  useEffect(() => {
    const list = trackPane.current;
    const row = list?.querySelector<HTMLElement>('[aria-current="true"]');
    if (followTracks && list && row)
      list.scrollTo({
        top: row.offsetTop - list.clientHeight / 3,
        behavior: reducedMotion ? "instant" : "smooth",
      });
  }, [track?.id, active, followTracks, data?.tracks, reducedMotion]);
  function seek(value: number) {
    if (active) p.seek(value);
    else p.load(episode, value);
  }
  const filtered =
    data?.cues.filter((c) =>
      `${c.text} ${cueText(c, lang)}`
        .toLocaleLowerCase()
        .includes(query.toLocaleLowerCase()),
    ) || [];
  const related = catalogue.episodes
    .filter(
      (e) =>
        e.id !== episode.id &&
        (e.station_id === episode.station_id ||
          e.languages.some((l) => episode.languages.includes(l))),
    )
    .slice(0, 3);
  return (
    <>
      <div className="episode-page studio-page">
        <header className="studio-summary">
          <Artwork episode={episode} />
          <div className="studio-title">
            <p className="studio-origin">
              <a href={route("station", episode.station_id)}>
                {episode.station_name}
              </a>
              <span aria-hidden="true"> / </span>
              <time>{date(episode.broadcast_date, lang)}</time>
            </p>
            <h1>
              <a href={route("show", episode.show_id)}>
                {episode.show_title || title(episode)}
              </a>
            </h1>
            <p dir="auto">{title(episode)}</p>
          </div>
          <div className="studio-actions">
            <IconButton
              name="plus"
              label={t("queue")}
              onClick={() => p.enqueue(episode)}
            />
            <IconButton
              name="share"
              label={t("share")}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(
                    location.origin +
                      location.pathname +
                      route("episode", episode.id, ms),
                  );
                  notify(t("copied"));
                } catch {
                  notify(t("copyFailed"));
                }
              }}
            />
          </div>
        </header>
        <section className="studio-player" aria-label={t("openPlayer")}>
          <Spectrogram
            recordingId={episode.recording?.id}
            duration={duration}
            ms={ms}
            peaks={active ? p.peaks : peaks}
            annotations={data}
            onSeek={seek}
          >
            <div className="studio-transport" ref={transport}>
              {active ? <Transport /> : <PlayButton episode={episode} large />}
              <div className="studio-time" dir="ltr">
                <strong>{clock(ms)}</strong>
                <span>/ {clock(duration)}</span>
              </div>
              <div
                className="studio-current-track"
                aria-live="polite"
                aria-atomic="true"
              >
                <span className="sr-only">{t("currentTrack")}</span>
                <strong dir="auto">
                  {track
                    ? trackIdentity(track).title || t("programmeAudio")
                    : cue
                      ? t("speech")
                      : t("programmeAudio")}
                </strong>
                <span dir="auto">
                  {track
                    ? trackIdentity(track).artist || episode.station_name
                    : episode.station_name}
                </span>
              </div>
              <div className="studio-tools">
                <label className="speed">
                  <span className="sr-only">{t("speed")}</span>
                  <select
                    value={p.speed}
                    onChange={(e) => p.setSpeed(+e.target.value)}
                  >
                    {[0.75, 1, 1.25, 1.5, 2].map((n) => (
                      <option key={n} value={n}>
                        {n}×
                      </option>
                    ))}
                  </select>
                </label>
                <div className="volume">
                  <IconButton
                    name={p.volume ? "volume" : "muted"}
                    label={t("volume")}
                    onClick={() => p.setVolume(p.volume ? 0 : 0.8)}
                  />
                  <input
                    aria-label={t("volume")}
                    type="range"
                    min="0"
                    max="1"
                    step=".01"
                    value={p.volume}
                    onChange={(e) => p.setVolume(+e.target.value)}
                  />
                </div>
                <DeviceButton />
                <IconButton
                  name="queue"
                  label={`${t("next")} (${p.queue.length})`}
                  onClick={() => p.setQueueOpen(true)}
                />
              </div>
            </div>
            {active && p.buffering && (
              <p className="studio-message" role="status">
                {t("loading")}
              </p>
            )}
            {active && p.error && (
              <p className="error studio-message" role="alert">
                {p.error}
              </p>
            )}
            <Caption cue={cue} />
          </Spectrogram>
        </section>
        <div className="listening-reading">
          <section
            className="transcript-section"
            aria-labelledby="transcript-heading"
          >
            <div className="reader-heading">
              <h2 id="transcript-heading">{t("transcript")}</h2>
              <LanguageSelect />
            </div>
            <p className="muted reader-hint">{t("transcriptHint")}</p>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            {!data && !error && <Empty>{t("loading")}</Empty>}
            {data && (
              <>
                <div className="transcript-tools">
                  <input
                    aria-label={t("search")}
                    placeholder={t("search")}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <label>
                    <input
                      type="checkbox"
                      checked={follow}
                      onChange={(e) => setFollow(e.target.checked)}
                    />
                    {t("follow")}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={bilingual}
                      onChange={(e) => setBilingual(e.target.checked)}
                    />
                    {t("bilingual")}
                  </label>
                </div>
                <p className="annotation-notice">{t("automaticHint")}</p>
                <div
                  ref={pane}
                  onWheel={() => setFollow(false)}
                  onTouchMove={() => setFollow(false)}
                  onKeyDown={(e) => {
                    if (
                      [
                        "ArrowDown",
                        "ArrowUp",
                        "PageDown",
                        "PageUp",
                        "Home",
                        "End",
                      ].includes(e.key)
                    )
                      setFollow(false);
                  }}
                  className="transcript-pane"
                  tabIndex={0}
                  aria-label={t("transcript")}
                >
                  {filtered.length ? (
                    filtered.map((c) => (
                      <button
                        ref={c.id === cue?.id ? activeCue : undefined}
                        key={c.id}
                        aria-current={
                          active && c.id === cue?.id ? "true" : undefined
                        }
                        className={
                          "cue " + (active && c.id === cue?.id ? "active" : "")
                        }
                        onClick={() => seek(c.start_ms)}
                      >
                        <time>{clock(c.start_ms)}</time>
                        <span>
                          <span lang={cueLanguage(c, lang)} dir="auto">
                            {cueText(c, lang)}
                          </span>
                          {bilingual &&
                            lang !== "original" &&
                            lang !== c.lang && (
                              <small lang={c.lang} dir="auto">
                                {c.text}
                              </small>
                            )}
                          {lang !== "original" &&
                            lang !== c.lang &&
                            !c.translations[lang] && (
                              <small className="translation-note">
                                {t("captionMissing")}
                              </small>
                            )}
                        </span>
                      </button>
                    ))
                  ) : (
                    <Empty>{t("noResults")}</Empty>
                  )}
                </div>
              </>
            )}
          </section>
          <section
            className="tracklist-section"
            aria-labelledby="tracklist-heading"
          >
            <div className="reader-heading">
              <h2 id="tracklist-heading">
                {t("tracks")}{" "}
                <span className="track-count">{data?.tracks.length || 0}</span>
              </h2>
            </div>
            <label className="follow-control">
              <input
                type="checkbox"
                checked={followTracks}
                onChange={(e) => setFollowTracks(e.target.checked)}
              />
              {t("trackFollow")}
            </label>
            <div
              className="tracks-list"
              ref={trackPane}
              tabIndex={0}
              aria-label={t("tracks")}
              onWheel={() => setFollowTracks(false)}
              onTouchMove={() => setFollowTracks(false)}
              onKeyDown={(e) => {
                if (
                  [
                    "ArrowDown",
                    "ArrowUp",
                    "PageDown",
                    "PageUp",
                    "Home",
                    "End",
                  ].includes(e.key)
                )
                  setFollowTracks(false);
              }}
            >
              {data?.tracks.length ? (
                data.tracks.map((tr, i) => (
                  <TrackRow
                    key={tr.id}
                    track={tr}
                    index={i}
                    active={active && track?.id === tr.id}
                    onSeek={seek}
                  />
                ))
              ) : (
                <Empty>{data ? t("noArtist") : t("loading")}</Empty>
              )}
            </div>
          </section>
        </div>
        <div className="episode-columns">
          <section className="annotation-panel" aria-labelledby="notes-heading">
            <h2 id="notes-heading">{t("notes")}</h2>

            <div className="notes-panel">
              <p className="muted">{t("noteHint")}</p>
              {user ? (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!episode.recording) return;
                    setSaving(true);
                    try {
                      await addNote(episode.recording.id, ms, note);
                      setNote("");
                    } catch (err) {
                      notify((err as Error).message);
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  <label className="note-at">
                    <Icon name="note" size={16} />
                    {clock(ms)}
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={t("notePlaceholder")}
                      aria-label={t("notes")}
                      required
                      maxLength={4000}
                    />
                  </label>
                  <button
                    className="button primary"
                    disabled={saving || !note.trim()}
                  >
                    {t("saveNote")}
                  </button>
                </form>
              ) : (
                <a className="button" href="#/account">
                  {t("noteLogin")}
                </a>
              )}
              {user && <NoteList recordingId={episode.recording?.id} />}
            </div>
          </section>
          <aside className="episode-about">
            <span className="eyebrow">{t("about")}</span>
            <h2>{episode.show_title}</h2>
            <p dir="auto">{description(episode)}</p>
            {localization.episodes[episode.id]?.automatic && (
              <small className="translation-note">{t("translated")}</small>
            )}
            <dl>
              <div>
                <dt>{t("stations")}</dt>
                <dd>
                  <a href={route("station", episode.station_id)}>
                    {episode.station_name}
                  </a>
                </dd>
              </div>
              <div>
                <dt>{t("languages")}</dt>
                <dd>
                  {episode.languages.map((l) => language(l, lang)).join(", ") ||
                    "—"}
                </dd>
              </div>
              <div>
                <dt>{t("broadcast")}</dt>
                <dd>{date(episode.broadcast_date, lang)}</dd>
              </div>
            </dl>
            {episode.page_url && (
              <a
                className="text-link"
                target="_blank"
                rel="noreferrer"
                href={episode.page_url}
              >
                {t("source")}
                <Icon name="arrow" size={16} />
              </a>
            )}
          </aside>
        </div>
      </div>
      {related.length > 0 && (
        <section className="related">
          <SectionHead label={t("explore")} />
          <EpisodeGrid episodes={related} />
        </section>
      )}
    </>
  );
}
